/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 201906029 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { AppParameterService } from '../app-parameter.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { AppParameter, JobSource } from 'src/app/_model/app-parameter';
import { AppParameterDaoService } from '../../dao/app-parameter-dao.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { CommunicationService } from 'src/app/_services/communication/logic/commumication.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { LBSUtils } from 'src/app/_util/lbs-utils';
import {
  IMSObjectType,
  LabelFormatSetting,
  ObjectGroupingSetting,
} from 'src/app/_model/ims-object';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads App Parameters
 */
@Injectable()
export class AppParameterLogicService implements AppParameterService {
  public appParameters$ = new BehaviorSubject<AppParameter>(undefined);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private appParameterDaoService: AppParameterDaoService,
    private authenticationService: AuthenticationService,
    private commService: CommunicationService
  ) {
    this.initialize();
  }

  getGroupingInfoForObject(objRef: IMSObjectType): ObjectGroupingSetting {
    if (objRef == undefined) {
      return undefined;
    }
    let obj = objRef.toLowerCase().split('/')[1];
    let appParameter = this.appParameters$.getValue();

    if (appParameter?.[`imsmc.labels.${obj}.grouping.attribute`] == undefined) {
      return undefined;
    }

    return {
      attribute: appParameter[`imsmc.labels.${obj}.grouping.attribute`],
      prefix: appParameter[`imsmc.labels.${obj}.grouping.prefix`],
    };
  }

  getFormatLabelForObject(objRef: IMSObjectType): LabelFormatSetting {
    if (objRef == undefined) {
      return undefined;
    }
    let obj = objRef.toLowerCase().split('/')[1];

    let labelSettings: LabelFormatSetting = undefined;
    let appParameter = this.appParameters$.getValue();

    //  Display Label / First Line

    if (
      appParameter &&
      appParameter[`imsmc.labels.${obj}.format`] &&
      appParameter[`imsmc.labels.${obj}.keys`]
    ) {
      labelSettings = {};
      labelSettings.primary = {
        value: appParameter[`imsmc.labels.${obj}.format`],
        databagKeys: appParameter[`imsmc.labels.${obj}.keys`].split(','),
        staticColor: appParameter[`imsmc.labels.${obj}.staticcolor`],
        dynamicColor: appParameter[`imsmc.labels.${obj}.dynamiccolor`],
      };
    }

    // Secondary Label / Second Line

    if (
      appParameter &&
      appParameter[`imsmc.labels.${obj}.secondary.format`] &&
      appParameter[`imsmc.labels.${obj}.secondary.keys`]
    ) {
      if (labelSettings == undefined) {
        labelSettings = {};
      }
      labelSettings.secondary = {
        value: appParameter[`imsmc.labels.${obj}.secondary.format`],
        databagKeys:
          appParameter[`imsmc.labels.${obj}.secondary.keys`].split(','),
        staticColor: appParameter[`imsmc.labels.${obj}.secondary.staticcolor`],
        dynamicColor:
          appParameter[`imsmc.labels.${obj}.secondary.dynamiccolor`],
      };
    }

    // Tertiary Label / Third line

    if (
      appParameter &&
      appParameter[`imsmc.labels.${obj}.tertiary.format`] &&
      appParameter[`imsmc.labels.${obj}.tertiary.keys`]
    ) {
      if (labelSettings == undefined) {
        labelSettings = {};
      }
      labelSettings.tertiary = {
        value: appParameter[`imsmc.labels.${obj}.tertiary.format`],
        databagKeys:
          appParameter[`imsmc.labels.${obj}.tertiary.keys`].split(','),
        staticColor: appParameter[`imsmc.labels.${obj}.tertiary.staticcolor`],
        dynamicColor: appParameter[`imsmc.labels.${obj}.tertiary.dynamiccolor`],
      };
    }

    // Keywords

    if (
      appParameter &&
      appParameter[`imsmc.labels.${obj}.keywords.format`] &&
      appParameter[`imsmc.labels.${obj}.keywords.keys`]
    ) {
      if (labelSettings == undefined) {
        labelSettings = {};
      }
      labelSettings.keywords = {
        value: appParameter[`imsmc.labels.${obj}.keywords.format`],
        databagKeys:
          appParameter[`imsmc.labels.${obj}.keywords.keys`].split(','),
      };
    }

    // Grouping
    if (
      appParameter &&
      appParameter[`imsmc.labels.${obj}.grouping.attribute`]
    ) {
      if (labelSettings == undefined) {
        labelSettings = {};
      }
      labelSettings.grouping = {
        attribute: appParameter[`imsmc.labels.${obj}.grouping.attribute`],
        prefix: appParameter[`imsmc.labels.${obj}.grouping.prefix`],
      };
    }

    // sorting
    if (
      appParameter &&
      appParameter[`imsmc.labels.${obj}.sorting.attribute`]
    ) {
      if (labelSettings == undefined) {
        labelSettings = {};
      }
      labelSettings.sorting = {
        attribute: appParameter[`imsmc.labels.${obj}.sorting.attribute`],
        order: appParameter[`imsmc.labels.${obj}.sorting.order`]
      };
    }

    // Icon
    if (
      appParameter &&
      (appParameter[`imsmc.labels.${obj}.icon.dynamicicon`] ||
        appParameter[`imsmc.labels.${obj}.icon.staticicon`])
    ) {
      if (labelSettings == undefined) {
        labelSettings = {};
      }
      labelSettings.icon = {
        dynamicIcon: appParameter[`imsmc.labels.${obj}.icon.dynamicicon`],
        staticIcon: appParameter[`imsmc.labels.${obj}.icon.staticicon`],
        size: appParameter[`imsmc.labels.${obj}.icon.size`],
        library: appParameter[`imsmc.labels.${obj}.icon.library`],
        dynamicColor: appParameter[`imsmc.labels.${obj}.icon.dynamiccolor`],
        staticColor: appParameter[`imsmc.labels.${obj}.icon.staticcolor`],
      };
    }
    return labelSettings;
  }

  // Resetable
  public async reset() {
    await this.appParameterDaoService.reset();
  }

  public async clear() {
    this.appParameterDaoService.clear();
    this.appParameters$.next(undefined);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    // No clear() upfront: loadRemoteAppParameters overwrites the stored parameters
    // on success, and a failed download must not leave the app without any parameters
    await this.loadRemoteAppParameters(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalAppParameters();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions

  private async loadLocalAppParameters() {
    try {
      this.logger.log(this.constructor.name, '- Load local AppParameters');
      let parameters: AppParameter =
        await this.appParameterDaoService.getAppParameters();
      this.logger.log(this.constructor.name, '- App Parameters:', parameters);
      this.appParameters$.next(parameters);

      this.setDependentSettings(parameters);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local AppParameters: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteAppParameters(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load new App Parameters from IMS'
      );

      var parameters: AppParameter;
      try {
        let data: any = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'appparameters',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );

        this.logger.log(this.constructor.name, 'Downloaded Data:', data);

        parameters = new AppParameter(data);

        this.logger.log(
          this.constructor.name,
          'Downloaded App Parameters: ',
          parameters
        );

        for (let [key, value] of Object.entries(parameters)) {
          if (typeof value === 'string') {
            if (value === 'true') {
              parameters[key] = true;
            } else if (value === 'false') {
              parameters[key] = false;
            }
          }
        }

        await this.appParameterDaoService.saveAppParameters(parameters);
        this.appParameters$.next(parameters);
        this.setDependentSettings(parameters);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading App Parameters: ',
          error?.message
        );
        throw error;
      }
    }
  }

  private setDependentSettings(parameters: AppParameter) {
    this.remoteActionService.appParams = parameters;
    this.commService.maxWidth = parameters
      ? parameters.imageScalingMaxWidth
      : undefined;
    this.commService.maxHeight = parameters
      ? parameters.imageScalingMaxHeight
      : undefined;
    this.commService.timeout =
      parameters?.['imsmc.connection.timeout'] ?? 30000;
  }

  async initializeCompleted(): Promise<void> {}
}
